import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import MiaPlatformSelector from './MiaPlatformSelector';
import MiaPlatformDataService from '../services/MiaPlatformDataService';
import './MobileCanvasDrawer.css';

/**
 * Mobile Canvas Drawer Component
 * Provides mobile access to Canvas features like:
 * - Mia Platform selectors (Organization, Project, Revision)
 * - Source code repository access
 * - GitLab integration
 * - Preview controls
 */
const MobileCanvasDrawer = ({
  isOpen,
  onClose
}) => {
  const [activeSection, setActiveSection] = useState('project');
  
  // Mia Platform state
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(() => {
    const saved = localStorage.getItem('mia_selectedTenant');
    return saved ? JSON.parse(saved) : null;
  });
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantsError, setTenantsError] = useState(null);
  
  const [projects, setProjects] = useState([]);
  const [projectsPagination, setProjectsPagination] = useState(null);
  const [projectsCurrentPage, setProjectsCurrentPage] = useState(1);
  const [projectsSearchQuery, setProjectsSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('mia_selectedProject');
    return saved ? JSON.parse(saved) : null;
  });
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState(() => {
    const saved = localStorage.getItem('mia_selectedRevision');
    return saved ? JSON.parse(saved) : null;
  });
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [revisionsError, setRevisionsError] = useState(null);
  
  const [configLoading, setConfigLoading] = useState(false);

  // Load tenants when drawer opens
  useEffect(() => {
    if (isOpen && tenants.length === 0 && !tenantsLoading) {
      loadTenants();
    }
  }, [isOpen]);

  // Load projects when tenant is selected
  useEffect(() => {
    if (selectedTenant && isOpen) {
      loadProjects(selectedTenant.id, '', 1, false);
    }
  }, [selectedTenant, isOpen]);

  // Load revisions when project is selected
  useEffect(() => {
    if (selectedProject && isOpen) {
      loadRevisions(selectedProject.id);
    }
  }, [selectedProject, isOpen]);

  // Persist selections to localStorage
  useEffect(() => {
    if (selectedTenant) {
      localStorage.setItem('mia_selectedTenant', JSON.stringify(selectedTenant));
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('mia_selectedProject', JSON.stringify(selectedProject));
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedRevision) {
      localStorage.setItem('mia_selectedRevision', JSON.stringify(selectedRevision));
    }
  }, [selectedRevision]);

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      setTenantsError(null);
      const tenantsData = await MiaPlatformDataService.getTenants();
      const formattedTenants = tenantsData.map(tenant => 
        MiaPlatformDataService.formatTenant(tenant)
      );
      setTenants(formattedTenants);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      setTenantsError(error.message || 'Failed to load tenants');
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadProjects = async (tenantId, searchQuery = '', page = 1, append = false) => {
    try {
      setProjectsLoading(true);
      setProjectsError(null);
      const result = await MiaPlatformDataService.getProjects(tenantId, searchQuery, page);
      const formattedProjects = result.data.map(project => 
        MiaPlatformDataService.formatProject(project)
      );
      if (append) {
        setProjects(prev => [...prev, ...formattedProjects]);
      } else {
        setProjects(formattedProjects);
      }
      setProjectsPagination(result.pagination);
      setProjectsCurrentPage(page);
      if (!append && formattedProjects.length === 1) {
        setSelectedProject(formattedProjects[0]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjectsError(error.message || 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadRevisions = async (projectId) => {
    try {
      setRevisionsLoading(true);
      setRevisionsError(null);
      const revisionsData = await MiaPlatformDataService.getRevisions(projectId);
      const formattedRevisions = revisionsData.map(revision => 
        MiaPlatformDataService.formatRevision(revision)
      );
      setRevisions(formattedRevisions);
      if (formattedRevisions.length > 0 && !selectedRevision) {
        setSelectedRevision(formattedRevisions[0]);
      }
    } catch (error) {
      console.error('Failed to load revisions:', error);
      setRevisionsError(error.message || 'Failed to load revisions');
    } finally {
      setRevisionsLoading(false);
    }
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    setSelectedProject(null);
    setSelectedRevision(null);
    setProjects([]);
    setRevisions([]);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setSelectedRevision(null);
    setRevisions([]);
  };

  const handleProjectSearch = useCallback((searchQuery) => {
    if (selectedTenant) {
      setProjectsSearchQuery(searchQuery);
      setProjectsCurrentPage(1);
      // Only clear selection if user is actively searching (not when clearing search)
      // Don't clear when searchQuery is empty (programmatic clear after selection)
      if (searchQuery && searchQuery !== projectsSearchQuery) {
        setSelectedProject(null);
        setSelectedRevision(null);
        setRevisions([]);
      }
      loadProjects(selectedTenant.id, searchQuery, 1, false);
    }
  }, [selectedTenant, projectsSearchQuery]);

  const handleLoadMoreProjects = useCallback(() => {
    if (selectedTenant && projectsPagination?.has_more && !projectsLoading) {
      const nextPage = projectsCurrentPage + 1;
      loadProjects(selectedTenant.id, projectsSearchQuery, nextPage, true);
    }
  }, [selectedTenant, projectsPagination, projectsLoading, projectsCurrentPage, projectsSearchQuery]);

  const handleRevisionSelect = (revision) => {
    setSelectedRevision(revision);
  };

  const handleRefresh = () => {
    if (selectedTenant) {
      loadProjects(selectedTenant.id, projectsSearchQuery, 1, false);
    }
    if (selectedProject) {
      loadRevisions(selectedProject.id);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="mobile-canvas-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div className="mobile-canvas-drawer">
        {/* Header */}
        <div className="mobile-canvas-drawer-header">
          <h2 className="drawer-title">
            <Icon name="computer" size={20} />
            <span>{'AI Coder'}</span>
          </h2>
          <button
            className="drawer-close-btn"
            onClick={onClose}
            type="button"
            aria-label="Close drawer"
          >
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="drawer-section-tabs">
          <button
            className={`section-tab ${activeSection === 'project' ? 'active' : ''}`}
            onClick={() => setActiveSection('project')}
            type="button"
          >
            <Icon name="folder" size={16} />
            {'Project'}
          </button>
          <button
            className={`section-tab ${activeSection === 'code' ? 'active' : ''}`}
            onClick={() => setActiveSection('code')}
            type="button"
            disabled={!selectedProject}
          >
            <Icon name="code" size={16} />
            {'Code'}
          </button>
          <button
            className={`section-tab ${activeSection === 'preview' ? 'active' : ''}`}
            onClick={() => setActiveSection('preview')}
            type="button"
            disabled={!selectedProject}
          >
            <Icon name="eye" size={16} />
            {'Preview'}
          </button>
        </div>

        {/* Content */}
        <div className="drawer-content">
          {/* Project Section */}
          {activeSection === 'project' && (
            <div className="drawer-section">
              <div className="section-header">
                <h3>{'Select Project'}</h3>
                <p className="section-description">
                  {'Choose an organization, project, and revision to work with'}
                </p>
              </div>

              <div className="selectors-stack">
                <div className="selector-group">
                  <label className="selector-label">{'Organization'}</label>
                  <MiaPlatformSelector
                    emptyMessage="No organizations found"
                    error={tenantsError}
                    icon={<Icon name="organization" size={18} />}
                    items={tenants}
                    loading={tenantsLoading}
                    onSelect={handleTenantSelect}
                    placeholder="Select an organization"
                    searchable
                    selectedItem={selectedTenant}
                  />
                </div>

                <div className="selector-group">
                  <label className="selector-label">{'Project'}</label>
                  <MiaPlatformSelector
                    emptyMessage={selectedTenant ? 'No projects found' : 'Select an organization first'}
                    error={projectsError}
                    hasMore={projectsPagination?.has_more || false}
                    icon={<Icon name="folder" size={18} />}
                    items={projects}
                    loading={projectsLoading}
                    onLoadMore={handleLoadMoreProjects}
                    onSearch={handleProjectSearch}
                    onSelect={handleProjectSelect}
                    placeholder="Select a project"
                    searchable
                    selectedItem={selectedProject}
                  />
                </div>

                <div className="selector-group">
                  <label className="selector-label">{'Revision'}</label>
                  <MiaPlatformSelector
                    emptyMessage={selectedProject ? 'No revisions found' : 'Select a project first'}
                    error={revisionsError}
                    icon={<Icon name="document" size={18} />}
                    items={revisions}
                    loading={revisionsLoading}
                    onSelect={handleRevisionSelect}
                    placeholder="Select a revision"
                    searchable={false}
                    selectedItem={selectedRevision}
                  />
                </div>

                {selectedProject && selectedRevision && (
                  <button
                    className="refresh-config-btn"
                    disabled={configLoading}
                    onClick={handleRefresh}
                    type="button"
                  >
                    <Icon name={configLoading ? 'spinner' : 'refresh'} size={18} />
                    {'Refresh Configuration'}
                  </button>
                )}
              </div>

              {selectedProject && (
                <div className="project-info">
                  <div className="info-item">
                    <span className="info-label">{'Organization:'}</span>
                    <span className="info-value">{selectedTenant?.name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{'Project:'}</span>
                    <span className="info-value">{selectedProject?.name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{'Revision:'}</span>
                    <span className="info-value">{selectedRevision?.name || '-'}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Code Section */}
          {activeSection === 'code' && (
            <div className="drawer-section">
              <div className="section-header">
                <h3>{'Source Code'}</h3>
                <p className="section-description">
                  {'View project files and resources'}
                </p>
              </div>

              {selectedProject && selectedRevision ? (
                <div className="mobile-code-view">
                  <div className="info-box">
                    <Icon name="info" size={16} />
                    <p>
                      {'Source code browsing is optimized for desktop. On mobile, you can view project configuration and resources.'}
                    </p>
                  </div>
                  
                  <div className="project-summary">
                    <div className="summary-item">
                      <Icon name="organization" size={18} />
                      <div>
                        <div className="summary-label">{'Organization'}</div>
                        <div className="summary-value">{selectedTenant?.name}</div>
                      </div>
                    </div>
                    <div className="summary-item">
                      <Icon name="folder" size={18} />
                      <div>
                        <div className="summary-label">{'Project'}</div>
                        <div className="summary-value">{selectedProject?.name}</div>
                      </div>
                    </div>
                    <div className="summary-item">
                      <Icon name="document" size={18} />
                      <div>
                        <div className="summary-label">{'Revision'}</div>
                        <div className="summary-value">{selectedRevision?.name}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <Icon name="folder" size={48} />
                  <p>{'Select a project and revision from the Project tab to view source code'}</p>
                </div>
              )}
            </div>
          )}

          {/* Preview Section */}
          {activeSection === 'preview' && (
            <div className="drawer-section">
              <div className="section-header">
                <h3>{'Live Preview'}</h3>
                <p className="section-description">
                  {'Preview your application'}
                </p>
              </div>

              {selectedProject && selectedRevision ? (
                <div className="mobile-preview-view">
                  <div className="info-box">
                    <Icon name="info" size={16} />
                    <p>
                      {'Live preview is best experienced on desktop with a larger screen. For mobile, use your browser to access the deployed application.'}
                    </p>
                  </div>
                  
                  <div className="project-summary">
                    <div className="summary-item">
                      <Icon name="eye" size={18} />
                      <div>
                        <div className="summary-label">{'Preview Status'}</div>
                        <div className="summary-value">{'Available on desktop'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <Icon name="eye" size={48} />
                  <p>{'Select a project and revision from the Project tab to enable preview'}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="drawer-footer">
          <button
            className="footer-btn secondary"
            onClick={onClose}
            type="button"
          >
            {'Close'}
          </button>
        </div>
      </div>
    </>
  );
};

MobileCanvasDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default MobileCanvasDrawer;
